import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import Select from "react-select";
import { useOutletContext } from "react-router-dom";
import "../../styles/common/dashboardPage.scss";
import OverallDashboardView from "./OverallDashboardView";
import CourseDashboardView from "./CourseDashboardView";
import BatchDashboardView from "./BatchDashboardView";
import GlobalLoader from "../common/GlobalLoader";
import DashboardOverviewSection from "../common/DashboardOverviewSection";
import {
  getInstitutionCumulativeDashboardBatchOverview,
  getInstitutionCumulativeDashboardCourseOverview,
  getInstitutionCumulativeDashboardOverview,
} from "../../helper/apicalls/dashboard";
const InstitutionDashboardHome = () => {
  const { setHeaderName } = useOutletContext() || {};
  const [selectedCourseKey, setSelectedCourseKey] = useState("");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [apiOverallData, setApiOverallData] = useState(null);
  const [apiCourseData, setApiCourseData] = useState(null);
  const [apiBatchData, setApiBatchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const courseFilter = (arr) =>
    (Array.isArray(arr) ? arr : []).filter(
      (c) => !selectedCourseKey || String(c.course_key) === String(selectedCourseKey),
    );

  const batchFilter = (arr) =>
    (Array.isArray(arr) ? arr : []).filter(
      (b) => !selectedBatchId || String(b.batch_id) === String(selectedBatchId),
    );

  const toMinutes = (seconds) => {
    const v = Number(seconds);
    if (!Number.isFinite(v)) return 0;
    return Math.round((v / 60) * 100) / 100;
  };

  const emptyStats = useMemo(
    () => ({
      course_completion: { courses: [], leaderboard: [], batches: [] },
      active_recall: { courses: [], leaderboard: [], batches: [] },
      pages_completed: { courses: [], leaderboard: [], batches: [] },
      homework: { courses: [], leaderboard: [], batches: [] },
      quizzes: { courses: [], leaderboard: [], batches: [] },
      tests: { courses: [], leaderboard: [], batches: [] },
      adaptive_learning: { courses: [], leaderboard: [], batches: [] },
      time_spent: { courses: [], leaderboard: [], batches: [] },
      coins: { courses: [], leaderboard: [], batches: [] },
      xp: { courses: [], leaderboard: [], batches: [] },
      batch_completion: { batches: [], leaderboard: [] },
    }),
    [],
  );

  const selectedCourseData = useMemo(() => {
    if (apiBatchData) {
      if (apiBatchData?.stats) return apiBatchData;

      const selectedId = selectedBatchId ? String(selectedBatchId) : "";
      const students = Array.isArray(apiBatchData?.students) ? apiBatchData.students : [];

      const byHomeworkCompleted =
        Array.isArray(apiBatchData?.leaderboards?.students?.by_homework_completed)
          ? apiBatchData.leaderboards.students.by_homework_completed
          : [];

      const byPagesCompleted =
        Array.isArray(apiBatchData?.leaderboards?.students?.by_pages_completed)
          ? apiBatchData.leaderboards.students.by_pages_completed
          : [];

      const pagesCompletedStudents = students.map((s) => ({
        batch_id: selectedId,
        batch_name: s?.student_name ?? s?.studentName ?? "Student",
        total_pages_completed: Number(s?.total_completed_pages) || 0,
      }));

      const homeworkStudents = students.map((s) => ({
        batch_id: selectedId,
        batch_name: s?.student_name ?? s?.studentName ?? "Student",
        homework_completion_rate: Number(s?.homework_completed) || 0,
        total_homework_completed: Number(s?.homework_completed) || 0,
      }));

      return {
        ...apiBatchData,
        stats: {
          ...emptyStats,
          pages_completed: {
            batches: pagesCompletedStudents,
            leaderboard: byPagesCompleted.map((s) => ({
              batch_id: selectedId,
              batch_name: s?.student_name ?? s?.studentName ?? "Student",
              total_pages_completed: Number(s?.total_completed_pages) || 0,
            })),
          },
          homework: {
            batches: homeworkStudents,
            leaderboard: byHomeworkCompleted.map((s) => ({
              batch_id: selectedId,
              batch_name: s?.student_name ?? s?.studentName ?? "Student",
              homework_completion_rate: Number(s?.homework_completed) || 0,
              total_homework_completed: Number(s?.homework_completed) || 0,
            })),
          },
        },
      };
    }

    if (!apiCourseData) return null;
    if (apiCourseData?.stats) return apiCourseData;

    const batches = Array.isArray(apiCourseData?.batches) ? apiCourseData.batches : [];
    const byCompletion = Array.isArray(apiCourseData?.leaderboards?.batches?.by_completion)
      ? apiCourseData.leaderboards.batches.by_completion
      : [];

    const normalizedBatches = batches.map((b) => ({
      ...b,
      batch_id: b?.batch_id ?? b?.batchId ?? b?.id ?? "",
      batch_name: b?.batch_name ?? b?.name ?? "Batch",
      completion_percentage: Number(b?.completion_percentage) || 0,
    }));

    return {
      ...apiCourseData,
      stats: {
        ...emptyStats,
        batch_completion: {
          batches: normalizedBatches,
          leaderboard: byCompletion,
        },
      },
    };
  }, [apiBatchData, apiCourseData, emptyStats, selectedBatchId]);

  const hasCourseData = Boolean(selectedCourseKey && selectedCourseData);

  const overallStats = apiOverallData?.stats || emptyStats;

  useEffect(() => {
    if (typeof setHeaderName === "function") {
      setHeaderName("Institution Dashboard");
    }
  }, [setHeaderName]);

  useEffect(() => {
    const fetchOverall = async () => {
      const token = Cookies.get("institution_token");
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const res = await getInstitutionCumulativeDashboardOverview(token);
        setApiOverallData(res || null);
      } catch (e) {
        console.error("Failed to load institution cumulative overview", e);
        setError("Failed to load dashboard overview");
      } finally {
        setLoading(false);
      }
    };

    fetchOverall();
  }, []);

  useEffect(() => {
    const fetchCourse = async () => {
      const token = Cookies.get("institution_token");
      if (!token) return;
      setApiCourseData(null);
      setApiBatchData(null);
      if (!selectedCourseKey) return;
      setLoading(true);
      setError("");
      try {
        const res = await getInstitutionCumulativeDashboardCourseOverview(
          token,
          selectedCourseKey,
        );
        setApiCourseData(res || null);
      } catch (e) {
        console.error("Failed to load institution course overview", e);
        setError("Failed to load course overview");
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [selectedCourseKey]);

  useEffect(() => {
    const fetchBatch = async () => {
      const token = Cookies.get("institution_token");
      if (!token) return;
      setApiBatchData(null);
      if (!selectedBatchId) return;
      setLoading(true);
      setError("");
      try {
        const res = await getInstitutionCumulativeDashboardBatchOverview(
          token,
          selectedBatchId,
        );
        setApiBatchData(res || null);
      } catch (e) {
        console.error("Failed to load institution batch overview", e);
        setError("Failed to load batch overview");
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [selectedBatchId]);

  const courseOptions = useMemo(() => {
    const base = [{ value: "", label: "Overall" }];
    const sourceCourses = Array.isArray(apiOverallData?.courses) ? apiOverallData.courses : [];
    const derived = sourceCourses.map((c) => {
      const key = c?.course_key ?? c?.courseKey ?? c?.key ?? "";
      const label = c?.course_name ?? c?.name ?? `Course ${key}`;
      return { value: String(key), label };
    });
    return base.concat(derived.filter((o) => o.value));
  }, [apiOverallData]);

  const selectedCourseOption = useMemo(
    () => courseOptions.find((o) => o.value === String(selectedCourseKey)) || courseOptions[0],
    [courseOptions, selectedCourseKey],
  );

  const batchOptions = useMemo(() => {
    const base = [{ value: "", label: "Overall" }];
    if (!hasCourseData) return base;
    const courseBatches = Array.isArray(apiCourseData?.batches)
      ? apiCourseData.batches
      : Array.isArray(apiCourseData?.lists?.batches)
        ? apiCourseData.lists.batches
        : [];
    const derived = courseBatches.map((b) => {
      const value = b?.batch_id ?? b?.batchId ?? b?.id ?? "";
      const label = b?.batch_name ?? b?.name ?? `Batch ${value}`;
      return { value: String(value), label };
    });
    return base.concat(derived.filter((o) => o.value));
  }, [hasCourseData, apiCourseData]);

  const selectedBatchOption = useMemo(
    () => batchOptions.find((o) => o.value === String(selectedBatchId)) || batchOptions[0],
    [batchOptions, selectedBatchId],
  );

  const viewMode = selectedBatchId ? "batch" : selectedCourseKey ? "course" : "overall";

  return (
    <div className="dashboard-page">
      <GlobalLoader visible={loading} text="Loading dashboard..." />
      {/* {!loading && error ? <p>{error}</p> : null} */}
      <div className="dashboard-admin-selects-row">
        <div className="dashboard-admin-select">
          <label>Course</label>
          <Select
            value={selectedCourseOption}
            onChange={(opt) => {
              const nextKey = opt ? opt.value : "";
              setSelectedCourseKey(nextKey);
              setSelectedBatchId("");
            }}
            options={courseOptions}
            isClearable={false}
          />
        </div>

        <div className="dashboard-admin-select">
          <label>Batch</label>
          <Select
            value={selectedBatchOption}
            onChange={(opt) => {
              const nextBatchId = opt ? opt.value : "";
              setSelectedBatchId(nextBatchId);
            }}
            options={batchOptions}
            isClearable={false}
          />
        </div>
      </div>

      {viewMode === "overall" && (
        <DashboardOverviewSection overall={apiOverallData?.overall} showTotalInstitutions={false} />
      )}

      {viewMode === "overall" ? (
        <OverallDashboardView stats={overallStats} courses={apiOverallData?.courses} toMinutes={toMinutes} />
      ) : viewMode === "course" ? (
        <CourseDashboardView
          courseKey={selectedCourseKey}
          stats={selectedCourseData?.stats}
          overall={selectedCourseData?.overall}
          batches={selectedCourseData?.batches}
          subjectWiseStats={selectedCourseData?.subject_wise_stats}
          toMinutes={toMinutes}
        />
      ) : (
        <BatchDashboardView
          stats={selectedCourseData?.stats}
          overall={selectedCourseData?.overall}
          subjectWiseStats={selectedCourseData?.subject_wise_stats}
          studentHierarchyCompletion={selectedCourseData?.student_hierarchy_completion}
          teacherHierarchyCompletion={selectedCourseData?.teacher_hierarchy_completion}
          toMinutes={toMinutes}
        />
      )}
    </div>
  );
};

export default InstitutionDashboardHome;
